export const QUERY = {
  GOOGLE_LOCATION: 'google-location'
} as const

/**
 * The venue's place on Google, as the backend keeps it.
 *
 * `needs_reselect` means Google no longer recognises the stored place id
 * (it closed, merged or moved), so the map and the review link may point at
 * nothing until the owner picks the venue again.
 */
export interface GoogleLocation {
  placeId: string
  status: 'active' | 'needs_reselect'
  connectedAt: string
  /** A Maps Embed API URL, built by the backend with its own key. */
  embedUrl: string
  reviewUrl: string
}

export interface PlaceSuggestion {
  placeId: string
  mainText: string
  secondaryText: string
}

// POST /restaurants/{restaurantId}/google-location/autocomplete
export interface AutocompleteRequest {
  input: string
  sessionToken: string
  /** False widens the search beyond restaurants, cafés and bars. */
  restrictToFood: boolean
}

export interface AutocompleteResponse {
  suggestions: PlaceSuggestion[]
}

// POST /restaurants/{restaurantId}/google-location/select
export interface SelectRequest {
  placeId: string
  sessionToken: string
}

/** A place's address in the parts the venue's own address has. */
export interface PlaceAddress {
  /** Empty where Google has none, as for a village without streets. */
  street: string
  buildingNumber: string
  postalCode: string
  city: string
}

export interface SelectResponse {
  /** The place as it will be once saved; nothing is stored by the pick. */
  location: GoogleLocation
  /** Only here: the GET does not return it and it is not stored. */
  formattedAddress: string
  /**
   * The same address in parts, to fill in the venue's address with. Missing
   * from a core-api that predates it; the parts are then read from
   * `formattedAddress` (`addressFromFormatted`).
   */
  address?: PlaceAddress
}

// GET /restaurants/{restaurantId}/google-location
export interface GetLocationResponse {
  location: GoogleLocation | null
}

// PUT /restaurants/{restaurantId}/google-location
export interface SaveRequest {
  /** A place `select` looked up for this venue in the last day. */
  placeId: string
}

export interface SaveResponse {
  location: GoogleLocation
}

/** What the backend and the in-browser stand-in both answer. */
export interface GoogleLocationClient {
  autocomplete: (
    request: AutocompleteRequest,
    signal: AbortSignal
  ) => Promise<PlaceSuggestion[]>
  select: (request: SelectRequest) => Promise<SelectResponse>
  save: (request: SaveRequest) => Promise<GoogleLocation>
  getLocation: () => Promise<GoogleLocation | null>
  disconnect: () => Promise<void>
}
